import * as React from 'react';

import type { ScheduleBoardEmployeePerDiemAssignmentTypeVM } from 'ab-viewModels/scheduleBoardEmployeePerDiemAssignment.viewModel';

import type ScheduleBoardWorkOrderVM from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import { debounce } from 'af-utils/actions.util';
import * as ReduxUtils from 'af-utils/reduxForms.util';

import { Button, NumberInput } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import LabelWithColor from 'af-components/LabelWithColor';

interface OwnProps {
	index: number;
	isEditable: boolean;
	perDiemAssignments: ScheduleBoardEmployeePerDiemAssignmentTypeVM;
	onPerDiemClick: (workOrderId: number, checked: boolean, amount: Nullable<string>) => void;
	removeAssignment: (workOrderId: number, workOrderCode: string, resourceLookupId?: number) => void;
	showPerDiem: boolean;
	workOrder: ScheduleBoardWorkOrderVM;
	resourceLookupId?: number;
	onPerDiemAmountChange: (workOrderId: number, amount: string) => void;
	isDeletable: boolean;
}

type Props = OwnProps;

const NON_DELETABLE_WO_STATUSES = {
	[WorkOrderStatus.LOCKED]: true,
	[WorkOrderStatus.CANCELED]: true,
};

const NonAssignableResourceAssignment: React.FC<Props> = (props: Props) => {
	const {
		removeAssignment,
		workOrder,
		resourceLookupId,
		onPerDiemClick,
		perDiemAssignments,
		isEditable,
		isDeletable,
		index,
		showPerDiem,
		onPerDiemAmountChange,
	} = props;

	const canDelete = isDeletable && !workOrder.isPaused && !NON_DELETABLE_WO_STATUSES[workOrder.status];

	const perDiemAmount = React.useMemo(() => {
		return !!perDiemAssignments[workOrder.id] ? perDiemAssignments[workOrder.id].amount : null;
	}, [perDiemAssignments, workOrder.id]);

	const formattedPerDiemAmount = React.useMemo(() => {
		return !!perDiemAmount ? ReduxUtils.formatDecimalNumber(perDiemAmount) : '0';
	}, [perDiemAmount]);

	const [formattedValue, setFormattedValue] = React.useState<string | undefined>(formattedPerDiemAmount);
	const [perDiemToggled, setPerDiemToggled] = React.useState(!!perDiemAssignments[workOrder.id]);

	React.useEffect(() => {
		setPerDiemToggled(!!perDiemAssignments[workOrder.id]);
	}, [perDiemAssignments, workOrder.id]);

	const onRemoveAssignment = React.useCallback(() => {
		removeAssignment(workOrder.id, workOrder.code, resourceLookupId);
	}, [removeAssignment, resourceLookupId, workOrder]);

	const onPerDiemCheck = React.useCallback(() => {
		onPerDiemClick(workOrder.id, !perDiemToggled, perDiemAmount);
		setPerDiemToggled(!perDiemToggled);
		setFormattedValue('0');
	}, [onPerDiemClick, workOrder.id, perDiemToggled, perDiemAmount]);

	React.useEffect(() => {
		setPerDiemToggled(!!perDiemAmount);
		setFormattedValue(formattedPerDiemAmount);
	}, [formattedPerDiemAmount, perDiemAmount]);

	const updatePerDiemAmount = React.useCallback((value: string) => {
		return onPerDiemAmountChange(workOrder.id, value);
	}, [onPerDiemAmountChange, workOrder.id]);

	const debouncedSaveAmount = React.useMemo(() => {
		return debounce(updatePerDiemAmount, 300);
	}, [updatePerDiemAmount]);

	const onChangePerDiemAmount = React.useCallback((inputValue: string) => {
		const numericValue = inputValue.replace(/[^\d.]/g, '');
		const formattedInputValue = ReduxUtils.formatDecimalNumber(numericValue);

		const amountBeforeDecimal = numericValue.split('.')[0];
		if (amountBeforeDecimal.length < 5) {
			setFormattedValue(formattedInputValue);
			debouncedSaveAmount(numericValue);
		}
	}, [debouncedSaveAmount]);

	return (
		<div className="schedule-board-modal__resource__assignment">
			<div className="schedule-board-modal__resource__assignment__name">
				<div className="schedule-board-modal__resource__assignment__name__work-order">{workOrder.code}</div>
				<LabelWithColor color={workOrder.crewTypeColor} text={workOrder.crewTypeName} />
			</div>
			<div className="schedule-board-modal__resource__assignment__perDiem">
				{showPerDiem && perDiemAssignments &&
					<>
						<div className={'resource-lookup__per-diem'}>
							<Checkbox
								handleChange={onPerDiemCheck}
								id={`${workOrder.code}_${index}`}
								inline={false}
								isChecked={perDiemToggled}
								isDisabled={!isEditable}
								label="Per Diem"
							/>
						</div>
						{!!perDiemToggled &&
							<div className="resource-lookup__per-diem-amount">
								<NumberInput
									disabled={!isEditable}
									isZeroIfEmpty={true}
									onChange={onChangePerDiemAmount}
									placeholder="Amount"
									unit="$"
									value={formattedValue ?? ''}
									withThousandsSeparator={true}
								/>
							</div>
						}
					</>
				}
				{
					canDelete &&
					<Button
						icon="Delete"
						onClick={onRemoveAssignment}
						style="link-danger"
						tooltip="Delete"
					/>
				}
			</div>
		</div>
	);
};

export default React.memo(NonAssignableResourceAssignment);
